import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown, ArrowUpDown, FilterX, Copy } from "lucide-react";
import { toast } from "sonner";

const ALIGN = { right: "text-right", center: "text-center", left: "text-left" };
const valueOf = (col, row) => (col.value ? col.value(row) : row[col.key]);
const asText = (v) => (v == null ? "" : String(v)).toLowerCase();

function compare(a, b) {
  const na = Number(a), nb = Number(b);
  if (a !== "" && b !== "" && a != null && b != null && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return asText(a).localeCompare(asText(b), "id", { numeric: true });
}

const ScrollBtn = ({ icon: Icon, onClick, disabled, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
    <Icon size={14} />
  </button>
);

/** Table with click-to-sort headers, a filter input per column and button-driven horizontal scrolling. */
export function DataTable({ columns, rows, rowKey = (r) => r.id, testid = "data-table", maxHeight = "62vh", rowClassName }) {
  const { t } = useLang();
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [filters, setFilters] = useState({});
  const [scroll, setScroll] = useState({ left: false, right: false });
  const box = useRef(null);

  const view = useMemo(() => {
    const active = columns.filter((c) => c.filter !== false && filters[c.key]);
    let out = rows.filter((r) => active.every((c) => asText(valueOf(c, r)).includes(filters[c.key].toLowerCase())));
    const col = columns.find((c) => c.key === sort.key);
    if (col) out = [...out].sort((a, b) => compare(valueOf(col, a), valueOf(col, b)) * (sort.dir === "asc" ? 1 : -1));
    return out;
  }, [rows, columns, filters, sort]);

  const updateScroll = () => {
    const el = box.current;
    if (el) setScroll({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  };
  useEffect(() => {
    updateScroll();
    window.addEventListener("resize", updateScroll);
    // Container width also changes without a window resize (e.g. sidebar minimize/expand)
    const ro = typeof ResizeObserver !== "undefined" && box.current ? new ResizeObserver(updateScroll) : null;
    if (ro) { ro.observe(box.current); if (box.current.firstElementChild) ro.observe(box.current.firstElementChild); }
    return () => { window.removeEventListener("resize", updateScroll); ro?.disconnect(); };
  }, [view.length, columns.length]);
  const scrollBy = (dx) => box.current?.scrollBy({ left: dx, behavior: "smooth" });
  const page = () => (box.current?.clientWidth || 600) * 0.6;
  const toggleSort = (key) =>
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }));
  const hasFilter = Object.values(filters).some(Boolean);

  const copyTable = async () => {
    const cols = columns.filter((c) => c.key !== "actions" && typeof c.label === "string" && c.label);
    const cell = (c, r) => {
      const v = c.value ? c.value(r) : r[c.key];
      return v == null || typeof v === "object" ? "" : v;
    };
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const tsv = [cols.map((c) => c.label), ...view.map((r) => cols.map((c) => String(cell(c, r)).replace(/[\t\n]/g, " ")))]
      .map((line) => line.join("\t")).join("\n");
    const html = `<table border="1"><thead><tr>${cols.map((c) => `<th style="background:#0f172a;color:#fff;font-weight:bold">${esc(c.label)}</th>`).join("")}</tr></thead><tbody>${
      view.map((r) => `<tr>${cols.map((c) => `<td>${esc(cell(c, r))}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    try {
      if (window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/plain": new Blob([tsv], { type: "text/plain" }), "text/html": new Blob([html], { type: "text/html" }) })]);
      } else {
        await navigator.clipboard.writeText(tsv);
      }
      toast.success(t("copied"));
    } catch {
      toast.error("Clipboard error");
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50/60">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span data-testid={`${testid}-count`}>{view.length} / {rows.length} {t("rows")}</span>
          {hasFilter && (
            <button type="button" onClick={() => setFilters({})} className="inline-flex items-center gap-1 text-sky-700 hover:underline" data-testid={`${testid}-clear-filters`}>
              <FilterX size={12} />{t("clear_filters")}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={copyTable} data-testid={`${testid}-copy-btn`}
                  className="h-7 px-2 mr-2 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white text-xs text-slate-700 hover:bg-slate-100 transition-colors">
            <Copy size={12} />{t("copy_table")}
          </button>
          <ScrollBtn icon={ChevronsLeft} onClick={() => box.current?.scrollTo({ left: 0, behavior: "smooth" })} disabled={!scroll.left} testid={`${testid}-scroll-start`} />
          <ScrollBtn icon={ChevronLeft} onClick={() => scrollBy(-page())} disabled={!scroll.left} testid={`${testid}-scroll-left`} />
          <ScrollBtn icon={ChevronRight} onClick={() => scrollBy(page())} disabled={!scroll.right} testid={`${testid}-scroll-right`} />
          <ScrollBtn icon={ChevronsRight} onClick={() => box.current?.scrollTo({ left: box.current.scrollWidth, behavior: "smooth" })} disabled={!scroll.right} testid={`${testid}-scroll-end`} />
        </div>
      </div>
      <div ref={box} onScroll={updateScroll} className="overflow-auto" style={{ maxHeight }}>
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700 shadow-[0_1px_0_0_#e2e8f0]">
            <tr>
              {columns.map((c) => {
                const sortable = c.sort !== false && c.filter !== false;
                const Icon = sort.key !== c.key ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
                return (
                  <th key={c.key} className={`py-2 px-3 text-xs font-semibold whitespace-nowrap ${ALIGN[c.align || "left"]} ${c.thClass || ""}`}>
                    {sortable ? (
                      <button type="button" onClick={() => toggleSort(c.key)} data-testid={`${testid}-sort-${c.key}`}
                              className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-900 ${sort.key === c.key ? "text-sky-700" : ""}`}>
                        {c.label}<Icon size={11} className={sort.key === c.key ? "" : "opacity-40"} />
                      </button>
                    ) : c.label}
                  </th>
                );
              })}
            </tr>
            <tr className="bg-white">
              {columns.map((c) => (
                <th key={c.key} className="px-2 py-1.5 border-b border-slate-200">
                  {c.filter !== false && (
                    <input value={filters[c.key] || ""} onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })}
                           placeholder={t("filter_ph")} data-testid={`${testid}-filter-${c.key}`}
                           className="w-full min-w-[70px] h-7 rounded border border-slate-200 px-2 text-xs font-normal normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-sky-500" />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r) => (
              <tr key={rowKey(r)} className={`border-b border-slate-100 hover:bg-slate-50 ${rowClassName ? rowClassName(r) : ""}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`py-2 px-3 ${ALIGN[c.align || "left"]} ${c.className || ""}`}>
                    {c.render ? c.render(r) : valueOf(c, r) ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
            {view.length === 0 && (
              <tr><td colSpan={columns.length} className="py-10 text-center text-slate-400">{t("no_data")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
